import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn, isEmptyOrSpaces } from '@/utils'
import { useShallow } from 'zustand/react/shallow'
import { useSearchParams } from 'wouter'
import { useCADPageStore } from '@/state/cad'
import {
  useDeleteOperationStep,
  useDeletePartsFromOperation,
} from '@/services/queries/operation_steps'
import { useCADQuery } from '@/services/queries/cads'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useStepOrderNumber } from './hooks/useStepOrderNumber'

import { Button } from '@/components/ui/button'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CollapsibleTriangleIcon,
} from '@/components/ui/collapsible'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { OpenDocumentPageButton } from './OpenDocumentPageButton'
import { SecondaryActionsButton } from './SecondaryActionsButton'
import { StepCardScreenshotsButton } from './StepCardScreenshotsButton'
import { AddPartFromCADButton } from './AddPartFromCADButton'
import { StepPart } from './StepPart'
import { StepCardContainer } from './StepCardContainer'
import { StepCardNote } from './StepCardNote'

import type { Step } from '@/services/queries/operation_steps/types'
import { Selectable, useSelectable } from '../../hooks'
import { RenameDialog } from '@/components/core/RenameDialog'
import { useUpdateDocumentPage } from '@/services/queries/document_pages'
import { ASMTreeNode } from '@/state'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { EditableField } from '@/components/core/EditableField'
import { ConfirmationDialog } from '@/components/core/ConfirmationDialog'
import {
  useAssemblyTreeQuery,
  useUpdateCadVersionMutation,
} from '../../queries'

export const StepCard = ({
  stepNumber,
  step,
  suboperationCount,
  isReadOnly,
}: {
  stepNumber: number
  step: Step
  suboperationCount: number
  isReadOnly?: boolean
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isRenaming, setIsRenaming] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const { isLoading: isCadLoading, data: cadData } = useCADQuery()
  const { isLoading: isDocumentLoading, data: docData } = useDocumentPageQuery()
  const {
    isLoading: isLoadingOrderNumber,
    data: { orderNumber },
  } = useStepOrderNumber({
    step,
  })
  const { mutateAsync: deleteOperationStep, isPending: isDeletingOperation } =
    useDeleteOperationStep()
  const { mutate: updateOperation, isPending: isUpdatingOperation } =
    useUpdateDocumentPage()
  const { mutateAsync: deletePartsFromOperation } =
    useDeletePartsFromOperation()
  const activeOperationStep = useCADPageStore((state) => state.operationStep)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const updateQueryParams = useCallback(
    (newParams: { [key: string]: string | null }): void => {
      const updated = new URLSearchParams(searchParams.toString())

      Object.keys(newParams).forEach((key) => {
        const value = newParams[key]
        if (value === null) {
          updated.delete(key)
        } else {
          updated.set(key, value)
        }
      })

      setSearchParams(updated)
    },
    [searchParams, setSearchParams],
  )

  const isPartsExpanded = useMemo(
    () => step.template_values?.isExpanded ?? true,
    [step.template_values?.isExpanded],
  )
  const setIsPartsExpanded = useCallback(
    (isExpanded) => {
      updateOperation({
        documentPageId: step.id as string,
        values: {
          ...step,
          parent_id: step.parent as string,
          template_values: {
            ...step.template_values,
            isExpanded,
          },
        },
      })
    },
    [updateOperation, step],
  )

  const onOpenRenameDialog = useCallback(() => {
    setTimeout(() => setIsRenaming(true), 200)
  }, [])

  const onCloseRenameDialog = useCallback(() => {
    setTimeout(() => setIsRenaming(false), 200)
  }, [])

  const gltf = cadData.gltf
  const cadVersionId = cadData.version?.id
  const assemblyTree = cadData.rawAssemblyTree || { root: '', nodes: [] }
  const assemblyGroupIds = step.assembly_group_ids

  const isProjectTracker = useMemo(
    () => docData?.documentType === 'project_tracker',
    [docData?.documentType],
  )

  const isWorkInstructions = useMemo(
    () => docData?.documentType === 'work_instructions',
    [docData?.documentType],
  )

  const selectable = useSelectable({})
  const isStepSelected = useMemo(
    () => activeOperationStep?.stepId === step.id,
    [activeOperationStep?.stepId, step.id],
  )
  const activeSelectFromCADStep = useMemo(
    () => isStepSelected && activeOperationStep?.selectFromCad,
    [isStepSelected, activeOperationStep?.selectFromCad],
  )
  const parts = useMemo(
    () =>
      assemblyTree?.nodes?.filter((node) =>
        assemblyGroupIds.includes(node.uuid),
      ) || [],
    [assemblyTree?.nodes, assemblyGroupIds],
  )

  const selectedPartsIds = useMemo(
    () =>
      parts
        .filter((part) => selectable.isSelected(part.uuid))
        .map((part) => part.uuid),
    [selectable, parts],
  )

  const partsGroups = useMemo(
    () =>
      parts.reduce(
        (accum, part) => {
          const foundPart = accum.find((p) => p.name === part.display_name)
          if (foundPart) {
            foundPart.parts.push(part)
          } else {
            accum.push({
              name: part.display_name,
              parts: [part],
            })
          }
          return accum
        },
        [] as Array<{ name: string; parts: Array<ASMTreeNode> }>,
      ),
    [parts],
  )

  const selectedPartsIndexes = useMemo(() => {
    return partsGroups.reduce((accum, group, index) => {
      const isAllGroupSelected = group.parts.every((p) =>
        selectable.isSelected(p.uuid),
      )
      if (isAllGroupSelected) {
        accum.push(index)
      }
      return accum
    }, [] as Array<number>)
  }, [selectable, partsGroups])

  const onDelete = useCallback(async () => {
    const documentPageAssemblyReferenceIds = step.documentpageassemblygroupids
      .filter((id) => selectedPartsIds.includes(id.assembly_group_id))
      .map((ref) => ref.id) as Array<number>
    if (documentPageAssemblyReferenceIds.length) {
      if (step.id) {
        await deletePartsFromOperation({
          partsIndex: selectedPartsIndexes,
          cadVersionId: cadVersionId as string,
          assemblyGroupIds: selectedPartsIds,
          documentPageId: step.id,
          documentVersionId: step.document_version,
          documentPageAssemblyReferenceIds,
        })
      }
    }
  }, [
    deletePartsFromOperation,
    step,
    selectedPartsIndexes,
    selectedPartsIds,
    cadVersionId,
  ])

  const onDeleteHandler = useCallback(
    (e: KeyboardEvent) => {
      if (isEditing) {
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        void onDelete()
      }
    },
    [isEditing, onDelete],
  )

  useEffect(() => {
    if (selectedPartsIds?.length) {
      document.addEventListener('keydown', onDeleteHandler)
    }

    return () => {
      document.removeEventListener('keydown', onDeleteHandler)
    }
  }, [selectedPartsIds, onDeleteHandler])

  const isLoading = isCadLoading || isDocumentLoading || isLoadingOrderNumber

  const onDeleteOperation = useCallback(async () => {
    const isActive = activeOperationStep?.stepId === step.id
    if (isActive) {
      setCadPageState({
        operationStep: null,
      })
    }
    await deleteOperationStep({ stepId: step.id as string })
    const pageId = searchParams.get('page_id')
    if (pageId === step.id) {
      updateQueryParams({ page_id: null })
    }
  }, [
    activeOperationStep?.stepId,
    deleteOperationStep,
    searchParams,
    step.id,
    setCadPageState,
    updateQueryParams,
  ])

  const onCloseDialogHandler = useCallback(() => {
    setShowConfirmationModal(false)
  }, [])

  const stepLabel = isProjectTracker
    ? 'Note'
    : step.parent
      ? 'Suboperation'
      : 'Operation'
  const stepName = isEmptyOrSpaces(step.name)
    ? `${stepLabel} ${orderNumber}`
    : step.name

  if (isLoading) {
    return (
      <div className="border border-gray-300 rounded-lg p-3 bg-white text-sm">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }

  return (
    <>
      <ConfirmationDialog
        title="Are you sure?"
        onConfirm={async () => {
          setShowConfirmationModal(false)
          await onDeleteOperation()
        }}
        isOpen={showConfirmationModal}
        isLoading={isDeletingOperation}
        closeDialog={onCloseDialogHandler}
      >
        <div>
          <p>
            {stepName}
            {suboperationCount > 0 &&
              ` and its suboperation${suboperationCount > 1 ? 's' : ''}`}{' '}
            will be deleted.
          </p>
          <p>This action cannot be undone.</p>
        </div>
      </ConfirmationDialog>
      <StepCardContainer step={step} gltf={gltf} stepNumber={stepNumber}>
        <div
          data-testid={`step-card-${stepNumber}`}
          className="group flex items-center justify-between w-full"
        >
          <div className="flex items-center space-x-2 pl-1 flex-1 min-w-0">
            <button
              type="button"
              aria-label={
                isPartsExpanded ? 'Collapse operation' : 'Expand operation'
              }
              aria-expanded={isPartsExpanded}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                setIsPartsExpanded(!isPartsExpanded)
              }}
              className={cn(
                'h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 opacity-100 flex-shrink-0',
              )}
            >
              {isPartsExpanded ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>

            <div
              className={cn(
                'bg-gray-100 text-black rounded-lg px-2 pt-1 inline-block max-w-[80%]',
                {
                  'bg-primary-50 text-white':
                    isStepSelected && !activeSelectFromCADStep,
                  'bg-purple-800 text-white': activeSelectFromCADStep,
                },
              )}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="max-w-full">
                    {isReadOnly ? (
                      <div className="text-ellipsis overflow-hidden whitespace-nowrap truncate">
                        {stepName}
                      </div>
                    ) : (
                      <EditableField
                        value={stepName}
                        shouldStopPropagation={isStepSelected}
                        onSaveHandler={(name: string) => {
                          updateOperation({
                            documentPageId: step.id as string,
                            values: {
                              name,
                              template_values: step.template_values,
                              parent_id: step.parent as string,
                            },
                          })
                        }}
                      />
                    )}
                  </TooltipTrigger>
                  <TooltipContent className="bg-black text-white">
                    <p className="max-w-[250px] text-wrap">
                      {isEmptyOrSpaces(step.name)
                        ? `${stepLabel} ${orderNumber}`
                        : step.name}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <div className="mr-1">
              <StepCardScreenshotsButton documentPageId={step.id as string} />
            </div>
            <OpenDocumentPageButton documentPageId={step.id as string} />
            {isWorkInstructions && !isReadOnly && (
              <AddPartFromCADButton step={step} />
            )}
            {!isReadOnly && (
              <>
                <RenameDialog
                  isOpen={isRenaming}
                  isLoading={isUpdatingOperation}
                  closeDialog={onCloseRenameDialog}
                  onSaveName={(name: string) => {
                    updateOperation({
                      documentPageId: step.id as string,
                      values: {
                        name,
                        template_values: step.template_values,
                        parent_id: step.parent as string,
                      },
                    })
                  }}
                  initialName={
                    isEmptyOrSpaces(step.name)
                      ? `${stepLabel} ${orderNumber}`
                      : step.name
                  }
                />
                <SecondaryActionsButton
                  onRename={onOpenRenameDialog}
                  stepNumber={stepNumber}
                  onDelete={() => {
                    if (!parts.length && !suboperationCount) {
                      void onDeleteOperation()
                    } else {
                      setShowConfirmationModal(true)
                    }
                  }}
                />
              </>
            )}
          </div>
        </div>
        <div
          className={cn(
            'py-2 overflow-hidden',
            isPartsExpanded
              ? 'max-h-[2000px] translate-y-0'
              : 'max-h-0  -translate-y-1 pointer-events-none',
          )}
        >
          {gltf &&
            cadVersionId &&
            partsGroups.length > 0 &&
            partsGroups.map((group, i) => {
              return (
                <StepGroup
                  setIsEditing={setIsEditing}
                  key={i}
                  index={i}
                  group={group}
                  selectFromCad={Boolean(activeOperationStep?.selectFromCad)}
                  gltf={gltf}
                  isReadOnly={!!isReadOnly}
                  isActive={activeOperationStep?.stepId === step.id}
                  step={step}
                  selectable={selectable}
                  cadversionId={cadVersionId}
                />
              )
            })}
          {parts.length === 0 && (
            <div className="flex flex-col space-y-2 text-wrap px-2">
              <p className="text-center text-gray-500 py-3">
                No parts in operation
              </p>
            </div>
          )}
          {isProjectTracker && (
            <StepCardNote step={step} isReadOnly={isReadOnly} />
          )}
        </div>
        <div
          className={cn('flex items-center', {
            'justify-end': isPartsExpanded,
            'justify-between': !isPartsExpanded,
          })}
        >
          {!isPartsExpanded && (
            <span className="text-xs text-gray-500 select-none">
              {parts.length} part{parts.length === 1 ? '' : 's'} in operation
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            className="flex items-center space-x-0 text-primary-50 text-xs h-6 rounded-full px-0 hover:bg-white hover:text-primary-50"
            onMouseDown={(e) => {
              e.stopPropagation()
              setCadPageState({
                selectedParts: [],
                subAssemblyDocumentPageId: step.id as string,
              })
            }}
          >
            <span>Suboperations ({suboperationCount})</span>
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </StepCardContainer>
    </>
  )
}

type StepGroupProps = {
  group: {
    name: string
    parts: ASMTreeNode[]
  }
  selectFromCad: boolean
  gltf: GLTFObject
  isReadOnly: boolean
  isActive: boolean
  selectable: Selectable
  step: Step
  cadversionId: string
  index: number
  setIsEditing: (isEditing: boolean) => void
}
const StepGroup = ({
  group,
  selectFromCad,
  gltf,
  isReadOnly,
  isActive,
  step,
  selectable,
  cadversionId,
  index,
  setIsEditing,
}: StepGroupProps) => {
  const parts = useMemo(() => group.parts, [group.parts])
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const [isGroupHovered, setIsGroupHovered] = useState(false)
  const [isEditingGroupName, setIsEditingGroupName] = useState(false)
  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { data: cadData } = useCADQuery()
  const { mutateAsync: updateCadVersion } = useUpdateCadVersionMutation()

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )

  const { mutateAsync: deletePartsFromOperation } =
    useDeletePartsFromOperation()
  const onDelete = useCallback(
    async (partsUUIDs: Array<string>) => {
      const documentPageAssemblyReference =
        step.documentpageassemblygroupids.find((id) =>
          partsUUIDs.includes(id.assembly_group_id),
        )
      if (documentPageAssemblyReference?.id) {
        if (step.id) {
          await deletePartsFromOperation({
            partsIndex: parts.length > 1 ? undefined : [index],
            cadVersionId: cadversionId,
            assemblyGroupIds: partsUUIDs,
            documentPageId: step.id,
            documentVersionId: step.document_version,
            documentPageAssemblyReferenceIds: [
              documentPageAssemblyReference.id,
            ],
          })
        }
      }
    },
    [deletePartsFromOperation, step, index, parts.length, cadversionId],
  )

  const isGroupSelected = useMemo(
    () => parts.every((part) => selectable.isSelected(part.uuid)),
    [parts, selectable.size],
  )

  const areAllPartsHidden = useMemo(
    () => parts.every((part) => hiddenParts.includes(part.uuid)),
    [parts, hiddenParts],
  )

  const areAllPartsSelected = useMemo(
    () => parts.every((part) => selectable.isSelected(part.uuid)),
    [parts, selectable],
  )

  const areSomePartsSelected = useMemo(
    () =>
      parts.some((part) => selectable.isSelected(part.uuid)) &&
      !areAllPartsSelected,
    [parts, selectable, areAllPartsSelected],
  )

  const handleGroupCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const partUUIDs = parts.map((p) => p.uuid)
      const shouldSelect = !areAllPartsSelected

      if (shouldSelect) {
        // Select all parts in the group
        const currentSelected = selectable.selected
        const newPartsToAdd = partUUIDs.filter(
          (uuid) => !currentSelected.includes(uuid),
        )
        setCadPageState({
          selectedParts: [...currentSelected, ...newPartsToAdd],
          selectionContext: 'step',
        })
      } else {
        // Deselect all parts in the group
        const currentSelected = selectable.selected
        const newSelected = currentSelected.filter(
          (uuid) => !partUUIDs.includes(uuid),
        )
        setCadPageState({ selectedParts: newSelected })
        if (newSelected.length === 0) {
          setCadPageState({ selectionContext: null })
        }
      }
    },
    [parts, selectable, setCadPageState, areAllPartsSelected],
  )

  const toggleGroupVisibility = useCallback(() => {
    const shouldShow = areAllPartsHidden
    const partUUIDs = parts.map((p) => p.uuid)

    parts.forEach((part) => {
      gltf.setVisibility(part.uuid, shouldShow)
    })

    setCadPageState({
      hiddenParts: shouldShow
        ? hiddenParts.filter((uuid) => !partUUIDs.includes(uuid))
        : [...new Set([...hiddenParts, ...partUUIDs])],
    })
  }, [areAllPartsHidden, parts, gltf, hiddenParts, setCadPageState])

  const handleGroupRename = useCallback(
    async (newGroupName: string) => {
      if (!assemblyTree) return

      // Deep clone to create new array reference
      const partUUIDs = new Set(parts.map((p) => p.uuid))
      const tree = {
        ...assemblyTree,
        nodes: assemblyTree.nodes.map((n) =>
          partUUIDs.has(n.uuid) ? { ...n, display_name: newGroupName } : n,
        ),
      }

      if (assemblyTree && cadVersionId && documentVersionId) {
        await updateCadVersion({
          cadVersionId,
          documentVersionId,
          assemblyTree: tree,
        })
      }
    },
    [assemblyTree, parts, cadVersionId, documentVersionId, updateCadVersion],
  )

  const handleRemoveAllParts = useCallback(async () => {
    const partUUIDs = parts.map((p) => p.uuid)
    const documentPageAssemblyReferenceIds = step.documentpageassemblygroupids
      .filter((ref) => partUUIDs.includes(ref.assembly_group_id))
      .map((ref) => ref.id) as Array<number>

    if (documentPageAssemblyReferenceIds.length && step.id) {
      await deletePartsFromOperation({
        partsIndex: [index],
        cadVersionId: cadversionId,
        assemblyGroupIds: partUUIDs,
        documentPageId: step.id,
        documentVersionId: step.document_version,
        documentPageAssemblyReferenceIds,
      })
    }
  }, [parts, step, index, cadversionId, deletePartsFromOperation])

  const handleRenameAll = useCallback(() => {
    setIsEditingGroupName(true)
  }, [])

  return parts.length > 1 ? (
    <Collapsible>
      <ContextMenu>
        <ContextMenuTrigger
          disabled={isReadOnly}
          onMouseUp={(e) => {
            e.stopPropagation()
            selectable.onSelectEnd(e)
          }}
        >
          <div
            className={cn(
              'flex items-center justify-between font-semibold py-1 hover:bg-primary-20 cursor-pointer pr-3',
              {
                'bg-primary-20': isGroupSelected,
                'bg-purple-50': isGroupSelected && selectFromCad,
                'hover:bg-purple-80 cursor-not-allowed':
                  selectFromCad && isGroupSelected,
                'hover:bg-white cursor-not-allowed':
                  selectFromCad && !isGroupSelected,
              },
            )}
            onMouseEnter={() => setIsGroupHovered(true)}
            onMouseLeave={() => setIsGroupHovered(false)}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center"
            >
              {isGroupHovered || areAllPartsSelected || areSomePartsSelected ? (
                <Checkbox
                  checked={areAllPartsSelected}
                  indeterminate={areSomePartsSelected}
                  onMouseDown={handleGroupCheckboxClick}
                  className="ml-1 flex-shrink-0"
                />
              ) : (
                <div className="w-4 h-4 ml-1 flex-shrink-0"></div>
              )}
            </div>
            <div className="flex flex-1 gap-1">
              <CollapsibleTrigger
                onClick={(e) => e.stopPropagation()}
                onMouseUp={(e) => {
                  e.stopPropagation()
                  selectable.onSelectEnd(e)
                }}
                className="flex items-center flex-1 min-w-0"
              >
                <div className="flex items-center w-full min-w-0">
                  <CollapsibleTriangleIcon className="w-5 h-5 text-indigo-400 flex-shrink-0 ml-1" />
                  <div className="flex font-semibold w-full justify-between items-center min-w-0">
                    {isReadOnly ? (
                      <span
                        className={cn(
                          'text-ellipsis overflow-hidden max-w-[205px]',
                          {
                            'text-gray-400': areAllPartsHidden,
                          },
                        )}
                      >
                        {group.name}{' '}
                      </span>
                    ) : (
                      <EditableField
                        value={group.name}
                        onSaveHandler={(name) => {
                          void handleGroupRename(name)
                        }}
                        textClassNames={cn('max-w-[205px]', {
                          'text-gray-400': areAllPartsHidden,
                        })}
                        shouldStopPropagation={false}
                        isEditing={isEditingGroupName}
                        setIsEditing={setIsEditingGroupName}
                        onEdit={(isEditingGroup) => {
                          setIsEditing(isEditingGroup)
                        }}
                      />
                    )}
                    <span
                      className={cn('ml-1 flex-shrink-0', {
                        'text-gray-400': areAllPartsHidden,
                      })}
                    >
                      (x{parts.length})
                    </span>
                  </div>
                </div>
              </CollapsibleTrigger>
              <div onClick={(e) => e.stopPropagation()}>
                {isGroupHovered || areAllPartsHidden ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-auto w-auto p-1 min-h-[24px]"
                          onMouseDown={(e) => {
                            e.stopPropagation()
                            toggleGroupVisibility()
                          }}
                        >
                          {areAllPartsHidden ? (
                            <EyeSlashIcon className="w-4 h-4 text-gray-400" />
                          ) : (
                            <EyeIcon className="w-4 h-4 text-primary-50" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-white text-black shadow-lg border border-gray-200 z-[9999]">
                        <p>{areAllPartsHidden ? 'Show all' : 'Hide all'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <div className="w-6 h-6"></div>
                )}
              </div>
            </div>
          </div>
        </ContextMenuTrigger>
        {!isReadOnly && (
          <ContextMenuContent>
            <ContextMenuItem
              onMouseDown={(e) => {
                e.stopPropagation()
                handleRenameAll()
              }}
            >
              Rename
            </ContextMenuItem>
            <ContextMenuItem
              onMouseDown={(e) => {
                e.stopPropagation()
                void handleRemoveAllParts()
              }}
            >
              Remove all
            </ContextMenuItem>
          </ContextMenuContent>
        )}
      </ContextMenu>
      <CollapsibleContent>
        <div
          className={cn('pl-4', {
            'bg-primary-20': isGroupSelected,
          })}
        >
          {parts.map((part, i) => (
            <StepPart
              setIsEditing={setIsEditing}
              key={i}
              selectFromCad={selectFromCad}
              part={part}
              gltf={gltf}
              isReadOnly={isReadOnly}
              onMouseDown={selectable.handlers(part.uuid, 'step').onSelectStart}
              stepIsActive={isActive}
              isSelected={selectable.isSelected(part.uuid)}
              onDelete={onDelete}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  ) : (
    <StepPart
      setIsEditing={setIsEditing}
      selectFromCad={selectFromCad}
      part={parts[0]}
      gltf={gltf}
      isReadOnly={isReadOnly}
      onMouseDown={selectable.handlers(parts[0].uuid, 'step').onSelectStart}
      stepIsActive={isActive}
      isSelected={selectable.isSelected(parts[0].uuid)}
      onDelete={onDelete}
    />
  )
}
